import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  Alert,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";

// Helper function to format date
export const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const DeleteConfirmationDialog = ({
  open,
  onClose,
  onConfirm,
  user,
  deleteType,
}) => {
  const [understandRisk, setUnderstandRisk] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    if (deleteType === "complete" && !understandRisk) return;
    setLoading(true);
    await onConfirm();
    setLoading(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <WarningIcon color="warning" />
        Delete User: {user?.email}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" paragraph>
          Choose how you want to delete this user:
        </Typography>

        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight={500}>
            User: {user?.username || user?.email}
          </Typography>
          <Typography variant="caption">
            Created: {formatDate(user?.createdAt)} | Last Login:{" "}
            {formatDate(user?.lastLogin)}
          </Typography>
        </Alert>

        <Box
          sx={{
            p: 2,
            mb: 2,
            border: "1px solid",
            borderColor: "warning.main",
            borderRadius: 1,
            bgcolor: "warning.lighter",
          }}
        >
          <Typography
            variant="subtitle2"
            color="warning.dark"
            sx={{ fontWeight: 600, mb: 1 }}
          >
            Option A: Delete User Record Only
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • Removes the user record from DynamoDB
            <br />
            • User can sign in again and a new record will be created
            <br />• Cognito account remains intact
          </Typography>
        </Box>

        <Box
          sx={{
            p: 2,
            mb: 2,
            border: "1px solid",
            borderColor: "error.main",
            borderRadius: 1,
            bgcolor: "error.lighter",
          }}
        >
          <Typography
            variant="subtitle2"
            color="error.dark"
            sx={{ fontWeight: 600, mb: 1 }}
          >
            Option B: Delete User Completely
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • Removes user record from DynamoDB
            <br />
            • Disables user in Cognito (prevents sign-in)
            <br />
            • Marks record with `createdByUserId: "deleted"` for soft delete
            <br />• User cannot sign in again without admin intervention
          </Typography>
        </Box>

        {deleteType === "complete" && (
          <FormControlLabel
            control={
              <Checkbox
                checked={understandRisk}
                onChange={(e) => setUnderstandRisk(e.target.checked)}
                color="error"
              />
            }
            label="I understand the consequences of completely deleting this user"
            sx={{ mt: 2 }}
          />
        )}

        {loading && <LinearProgress sx={{ mt: 2 }} />}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          color={deleteType === "complete" ? "error" : "warning"}
          variant="contained"
          disabled={deleteType === "complete" && !understandRisk}
          startIcon={
            deleteType === "complete" ? <PersonOffIcon /> : <PersonRemoveIcon />
          }
        >
          {deleteType === "complete"
            ? "Delete Completely"
            : "Delete Record Only"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteConfirmationDialog;
